import { useCallback, useRef, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import {
  Clock,
  Upload,
  FileDown,
  Loader2,
  Search,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  Hammer,
  Sparkles,
  ChevronRight,
  Filter,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import AdminAfdEditor from "./AdminAfdEditor";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminAfdTab({ token }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [jobId, setJobId] = useState("");
  const [jobStatus, setJobStatus] = useState(""); // "", "running", "done", "error"
  const [buckets, setBuckets] = useState([]);
  const [header, setHeader] = useState(null);
  const [stats, setStats] = useState(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [openBucket, setOpenBucket] = useState(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all"); // all | issues | corrected | unmatched
  const [autoFixingAll, setAutoFixingAll] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const pickFile = (f) => {
    if (!f) return;
    const ok =
      f.name.toLowerCase().endsWith(".txt") ||
      f.name.toLowerCase().endsWith(".afd");
    if (!ok) {
      toast.error("Selecione um arquivo .txt ou .afd");
      return;
    }
    setFile(f);
    setJobId("");
    setJobStatus("");
    setBuckets([]);
    setHeader(null);
    setStats(null);
  };

  const loadBuckets = useCallback(
    async (jid) => {
      // The /buckets endpoint may answer 202 while the background
      // parse is still running (large AFDs). Poll up to ~3 minutes.
      const deadline = Date.now() + 3 * 60 * 1000;
      let attempts = 0;
      while (Date.now() < deadline) {
        attempts += 1;
        const resp = await axios.get(
          `${API}/admin/afd/job/${jid}/buckets`,
          { headers, validateStatus: (s) => s === 200 || s === 202 }
        );
        if (resp.status === 200) {
          const data = resp.data;
          setBuckets(data?.buckets || []);
          if (data?.header) setHeader(data.header);
          const matched = (data?.buckets || []).filter((b) => b.matched).length;
          const issues = (data?.buckets || []).filter(
            (b) => b.pares_incompletos > 0 || b.turnos_longos > 0
          ).length;
          const corrected = (data?.buckets || []).filter((b) => b.tem_correcoes)
            .length;
          setStats({
            total: data?.buckets?.length || 0,
            matched,
            unmatched: (data?.buckets?.length || 0) - matched,
            issues,
            corrected,
          });
          return;
        }
        // 202 — keep waiting
        if (attempts === 3) {
          toast.message(
            "Parseando AFD no servidor — pode demorar alguns segundos para arquivos grandes."
          );
        }
        await new Promise((r) => setTimeout(r, 2500));
      }
      throw new Error(
        "Tempo esgotado aguardando processamento do AFD. Tente novamente."
      );
    },
    [token]
  );

  const pollStatus = useCallback(
    async (jid) => {
      try {
        const { data } = await axios.get(
          `${API}/admin/afd/status/${jid}`,
          { headers }
        );
        setJobStatus(data?.status || "running");
      } catch {
        /* keep silent — buckets endpoint is what we need */
      }
    },
    [token]
  );

  const onAnalyze = async () => {
    if (!file) return;
    setAnalyzing(true);
    setBuckets([]);
    setStats(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const startResp = await axios.post(`${API}/admin/afd/start`, form, {
        headers,
      });
      const jid = startResp.data?.job_id;
      if (!jid) throw new Error("Job ID ausente");
      setJobId(jid);
      setJobStatus("running");

      // Buckets endpoint works as soon as job is created (raw_bytes already stored)
      await loadBuckets(jid);
      pollStatus(jid);
      toast.success("AFD carregado. Selecione um funcionário para auditar.");
    } catch (err) {
      toast.error(
        `Erro ao analisar AFD: ${err?.response?.data?.detail || err.message}`
      );
    } finally {
      setAnalyzing(false);
    }
  };

  const onAutoFixAll = async () => {
    if (!jobId) return;
    if (
      !window.confirm(
        "Aplicar auto-correção em TODOS os colaboradores 12x36 com inconsistências?\n\n" +
          "• Remove batidas duplicadas (≤3 min)\n" +
          "• Insere saída em ~12h após entradas com gap >18h\n" +
          "• Marca pares <4h como abono 'Inconsistência — revisar'\n" +
          "• Não toca em dias já corrigidos por você\n" +
          "• Funcionários daytime (4 batidas/dia) são ignorados"
      )
    )
      return;
    setAutoFixingAll(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/afd/job/${jobId}/auto-fix`,
        null,
        { headers }
      );
      const msg =
        `${data.buckets_touched} colaboradores corrigidos · ` +
        `${data.missing_punches_inserted} saídas inseridas · ` +
        `${data.dedup_removed} duplicadas removidas · ` +
        `${data.abono_days} abonos · ` +
        `${data.buckets_non_12x36} pulados (não 12x36)`;
      toast.success(msg, { duration: 7000 });
      await loadBuckets(jobId);
    } catch (err) {
      toast.error(
        `Erro no auto-corrigir: ${err?.response?.data?.detail || err.message}`
      );
    } finally {
      setAutoFixingAll(false);
    }
  };

  const onRebuild = async () => {
    if (!jobId) return;
    setRebuilding(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/afd/job/${jobId}/rebuild-zip`,
        null,
        { headers }
      );
      const kb = ((data?.size_bytes || 0) / 1024).toFixed(0);
      toast.success(`ZIP reconstruído com correções (${kb} KB).`);
      setJobStatus("done");
    } catch (err) {
      toast.error(
        `Erro ao reconstruir: ${err?.response?.data?.detail || err.message}`
      );
    } finally {
      setRebuilding(false);
    }
  };

  const onDownload = async () => {
    if (!jobId) return;
    setDownloading(true);
    try {
      // Make sure the job finished first (the initial empty-corrections ZIP)
      const st = await axios.get(`${API}/admin/afd/status/${jobId}`, {
        headers,
      });
      if (st.data?.status === "running") {
        toast.message("Aguardando processamento concluir...");
        const t0 = Date.now();
        while (Date.now() - t0 < 5 * 60 * 1000) {
          await new Promise((r) => setTimeout(r, 1500));
          const s2 = await axios.get(
            `${API}/admin/afd/status/${jobId}`,
            { headers }
          );
          if (s2.data?.status === "done") {
            setJobStatus("done");
            break;
          }
          if (s2.data?.status === "error") {
            throw new Error(s2.data?.message || "Falha na geração");
          }
        }
      } else if (st.data?.status === "error") {
        throw new Error(st.data?.message || "Job em erro");
      }
      const resp = await axios.get(
        `${API}/admin/afd/download/${jobId}`,
        { headers, responseType: "blob" }
      );
      const blob = new Blob([resp.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date()
        .toISOString()
        .replace(/[-:]/g, "")
        .replace(/T/, "_")
        .slice(0, 13);
      a.href = url;
      a.download = `espelhos_ponto_${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("ZIP baixado.");
    } catch (err) {
      toast.error(
        `Erro ao baixar: ${err?.response?.data?.detail || err.message}`
      );
    } finally {
      setDownloading(false);
    }
  };

  const visible = buckets.filter((b) => {
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = `${b.nome_db} ${b.nome_afd} ${b.matricula} ${b.cpf} ${b.pis}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filter === "issues")
      return b.pares_incompletos > 0 || b.turnos_longos > 0;
    if (filter === "corrected") return b.tem_correcoes;
    if (filter === "unmatched") return !b.matched;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="admin-afd-tab">
      <Card className="bg-white border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center font-heading text-base">
            <Clock className="w-4 h-4 mr-2 text-blue-600" />
            Converter AFD — Auditoria & Espelho de Ponto
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-4">
            Upload do AFD do REP → o sistema cruza com a base, identifica
            inconsistências e abre uma tela de <strong>auditoria por
            funcionário</strong>. Corrija marcações, adicione batidas
            esquecidas ou registre abonos, e regenere o ZIP final com tudo
            aplicado. Suporta Portarias <strong>1510/2009</strong> e{" "}
            <strong>671/2021</strong>.
          </p>

          <div
            className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center bg-slate-50/50 hover:bg-slate-50 transition cursor-pointer"
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.stopPropagation();
              pickFile(e.dataTransfer.files?.[0]);
            }}
            data-testid="afd-dropzone"
          >
            <input
              ref={fileRef}
              type="file"
              accept=".txt,.afd,text/plain"
              className="hidden"
              onChange={(e) => pickFile(e.target.files?.[0])}
              data-testid="afd-file-input"
            />
            <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
            <div className="text-sm font-medium text-slate-700">
              {file ? file.name : "Clique ou arraste o arquivo AFD aqui"}
            </div>
            <div className="text-xs text-slate-500 mt-1">
              .txt ou .afd · até 200 MB
            </div>
            {file ? (
              <div className="text-xs text-slate-500 mt-2">
                {(file.size / 1024).toFixed(0)} KB
              </div>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-2 mt-4">
            <Button
              onClick={onAnalyze}
              disabled={!file || analyzing}
              data-testid="afd-analyze-btn"
            >
              {analyzing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Search className="w-4 h-4 mr-2" />
              )}
              Analisar AFD
            </Button>

            {jobId ? (
              <>
                <Button
                  onClick={onAutoFixAll}
                  disabled={autoFixingAll || rebuilding || downloading}
                  variant="outline"
                  className="border-violet-300 text-violet-700 hover:bg-violet-50"
                  data-testid="afd-autofix-all-btn"
                >
                  {autoFixingAll ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Wand2 className="w-4 h-4 mr-2" />
                  )}
                  Auto-corrigir todos
                </Button>
                <Button
                  onClick={onRebuild}
                  disabled={rebuilding || downloading}
                  variant="outline"
                  data-testid="afd-rebuild-btn"
                >
                  {rebuilding ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Hammer className="w-4 h-4 mr-2" />
                  )}
                  Reconstruir ZIP
                </Button>
                <Button
                  onClick={onDownload}
                  disabled={rebuilding || downloading}
                  variant="secondary"
                  data-testid="afd-download-btn"
                >
                  {downloading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <FileDown className="w-4 h-4 mr-2" />
                  )}
                  Baixar ZIP
                </Button>
                {jobStatus === "running" ? (
                  <Badge variant="outline" className="self-center text-xs">
                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                    processando AFD inicial...
                  </Badge>
                ) : null}
                {jobStatus === "done" ? (
                  <Badge className="self-center bg-emerald-100 text-emerald-800 border-emerald-200 text-xs">
                    <CheckCircle2 className="w-3 h-3 mr-1" />
                    ZIP pronto
                  </Badge>
                ) : null}
              </>
            ) : null}
          </div>

          {header?.razao_social ? (
            <div className="text-xs text-slate-500 mt-3">
              <span className="font-semibold text-slate-700">Empresa:</span>{" "}
              {header.razao_social}
              {header.rep_serial ? ` · REP ${header.rep_serial}` : ""}
              {header.portaria ? ` · Portaria ${header.portaria}` : ""}
            </div>
          ) : null}
        </CardContent>
      </Card>

      {stats ? (
        <Card className="bg-white border-slate-200">
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
              <Stat label="Colaboradores" value={stats.total} />
              <Stat label="Identificados" value={stats.matched} tone="success" />
              <Stat
                label="Sem cadastro"
                value={stats.unmatched}
                tone={stats.unmatched ? "warning" : "neutral"}
              />
              <Stat
                label="Com inconsistência"
                value={stats.issues}
                tone={stats.issues ? "danger" : "neutral"}
              />
              <Stat
                label="Já corrigidos"
                value={stats.corrected}
                tone={stats.corrected ? "info" : "neutral"}
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap mb-3">
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar por nome, matrícula, CPF ou PIS..."
                className="h-9 text-xs max-w-sm"
                data-testid="afd-search-input"
              />
              <FilterPill
                active={filter === "all"}
                onClick={() => setFilter("all")}
                label="Todos"
                value="all"
                count={stats.total}
              />
              <FilterPill
                active={filter === "issues"}
                onClick={() => setFilter("issues")}
                label="Inconsistências"
                value="issues"
                count={stats.issues}
                tone="danger"
              />
              <FilterPill
                active={filter === "corrected"}
                onClick={() => setFilter("corrected")}
                label="Corrigidos"
                value="corrected"
                count={stats.corrected}
                tone="info"
              />
              <FilterPill
                active={filter === "unmatched"}
                onClick={() => setFilter("unmatched")}
                label="Sem cadastro"
                value="unmatched"
                count={stats.unmatched}
                tone="warning"
              />
            </div>

            <div className="border border-slate-200 rounded overflow-hidden">
              <div className="max-h-[560px] overflow-y-auto">
                <table className="w-full text-xs" data-testid="afd-buckets-table">
                  <thead className="bg-slate-100 sticky top-0 z-10">
                    <tr>
                      <th className="text-left py-2 px-3">Colaborador</th>
                      <th className="text-left py-2 px-3">Matrícula</th>
                      <th className="text-right py-2 px-3">Marcações</th>
                      <th className="text-right py-2 px-3">Pares</th>
                      <th className="text-left py-2 px-3">Status</th>
                      <th className="text-right py-2 px-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((b) => (
                      <tr
                        key={b.bucket_id}
                        className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                        onClick={() => setOpenBucket(b)}
                        data-testid={`afd-bucket-row-${b.bucket_id}`}
                      >
                        <td className="py-2 px-3">
                          <div className="font-medium text-slate-800">
                            {b.nome_db || b.nome_afd || "—"}
                          </div>
                          <div className="text-[10px] text-slate-400 font-mono">
                            {b.cpf ? `CPF ${b.cpf}` : `PIS ${b.pis}`}
                          </div>
                        </td>
                        <td className="py-2 px-3">{b.matricula || "—"}</td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {b.punches_raw}
                          {b.punches_eff !== b.punches_raw ? (
                            <span className="text-amber-600">
                              {" "}
                              → {b.punches_eff}
                            </span>
                          ) : null}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {b.pares_total}
                        </td>
                        <td className="py-2 px-3">
                          <div className="flex flex-wrap gap-1">
                            {!b.matched ? (
                              <span className="inline-flex items-center text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                                <AlertCircle className="w-3 h-3 mr-1" />
                                sem cadastro
                              </span>
                            ) : null}
                            {b.pares_incompletos > 0 ? (
                              <span className="inline-flex items-center text-[10px] bg-red-100 text-red-800 px-1.5 py-0.5 rounded">
                                <AlertTriangle className="w-3 h-3 mr-1" />
                                {b.pares_incompletos} incompleto
                                {b.pares_incompletos > 1 ? "s" : ""}
                              </span>
                            ) : null}
                            {b.turnos_longos > 0 ? (
                              <span className="inline-flex items-center text-[10px] bg-orange-100 text-orange-800 px-1.5 py-0.5 rounded">
                                <Clock className="w-3 h-3 mr-1" />
                                {b.turnos_longos} &gt;24h
                              </span>
                            ) : null}
                            {b.tem_correcoes ? (
                              <span className="inline-flex items-center text-[10px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">
                                <Sparkles className="w-3 h-3 mr-1" />
                                corrigido
                              </span>
                            ) : null}
                            {b.matched &&
                            !b.pares_incompletos &&
                            !b.turnos_longos &&
                            !b.tem_correcoes ? (
                              <span className="inline-flex items-center text-[10px] text-emerald-700">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                ok
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="py-2 px-3 text-right">
                          <ChevronRight className="w-4 h-4 text-slate-400 inline" />
                        </td>
                      </tr>
                    ))}
                    {!visible.length ? (
                      <tr>
                        <td colSpan={6} className="text-center text-slate-400 py-6">
                          Nenhum resultado para o filtro atual.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      {openBucket ? (
        <AdminAfdEditor
          open={!!openBucket}
          onClose={() => setOpenBucket(null)}
          jobId={jobId}
          bucket={openBucket}
          token={token}
          onSavedAny={() => jobId && loadBuckets(jobId)}
        />
      ) : null}
    </div>
  );
}

function Stat({ label, value, tone = "neutral" }) {
  const tones = {
    neutral: "bg-slate-50 text-slate-700 border-slate-200",
    success: "bg-emerald-50 text-emerald-700 border-emerald-200",
    warning: "bg-amber-50 text-amber-700 border-amber-200",
    danger: "bg-red-50 text-red-700 border-red-200",
    info: "bg-blue-50 text-blue-700 border-blue-200",
  };
  return (
    <div className={`rounded border px-3 py-2 ${tones[tone]}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">
        {label}
      </div>
      <div className="text-lg font-bold">{value}</div>
    </div>
  );
}

function FilterPill({ active, onClick, label, value, count, tone = "neutral" }) {
  const tones = {
    neutral: active
      ? "bg-slate-800 text-white border-slate-800"
      : "bg-white text-slate-700 border-slate-300",
    danger: active
      ? "bg-red-600 text-white border-red-600"
      : "bg-white text-red-700 border-red-200",
    warning: active
      ? "bg-amber-600 text-white border-amber-600"
      : "bg-white text-amber-700 border-amber-200",
    info: active
      ? "bg-blue-600 text-white border-blue-600"
      : "bg-white text-blue-700 border-blue-200",
  };
  return (
    <button
      onClick={onClick}
      className={`text-xs px-2.5 py-1 rounded-full border inline-flex items-center gap-1 ${tones[tone]}`}
      data-testid={`afd-filter-${value || label.toLowerCase()}`}
    >
      <Filter className="w-3 h-3" />
      {label}
      <span className="text-[10px] opacity-70">({count})</span>
    </button>
  );
}
